import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { ArrowLeft, Plus, Trash2, Edit2, X, Save, Loader2, Car } from 'lucide-react'
import { Link } from 'react-router-dom'

interface Vehicle {
  id: number
  year: number | null
  make: string | null
  model: string | null
  color: string | null
  license_plate: string
  plate_state: string | null
  is_primary: boolean
}

export default function ResidentVehicles() {
  const { token } = useAuth()
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ year: '', make: '', model: '', color: '', license_plate: '', plate_state: '', is_primary: false })

  useEffect(() => { fetchVehicles() }, [])

  async function fetchVehicles() {
    try {
      const res = await fetch('/api/auth/resident/vehicles', { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) setVehicles(await res.json())
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  function openAdd() {
    setForm({ year: '', make: '', model: '', color: '', license_plate: '', plate_state: '', is_primary: false })
    setEditId(null)
    setShowForm(true)
  }

  function openEdit(v: Vehicle) {
    setForm({
      year: v.year?.toString() || '',
      make: v.make || '',
      model: v.model || '',
      color: v.color || '',
      license_plate: v.license_plate,
      plate_state: v.plate_state || '',
      is_primary: v.is_primary,
    })
    setEditId(v.id)
    setShowForm(true)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    const body = { ...form, year: form.year ? parseInt(form.year) : null }
    try {
      const url = editId ? `/api/auth/resident/vehicles/${editId}` : '/api/auth/resident/vehicles'
      const method = editId ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      })
      if (res.ok) {
        setShowForm(false)
        fetchVehicles()
      }
    } catch (err) { console.error(err) }
    finally { setSaving(false) }
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this vehicle?')) return
    await fetch(`/api/auth/resident/vehicles/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    })
    fetchVehicles()
  }

  if (loading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></Link>
            <h1 className="text-lg font-semibold text-gray-900">My Vehicles</h1>
          </div>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> Add Vehicle
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {vehicles.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Car className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No vehicles registered yet.</p>
            <button onClick={openAdd} className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm">Add Your First Vehicle</button>
          </div>
        ) : (
          <div className="space-y-4">
            {vehicles.map((v) => (
              <div key={v.id} className="bg-white rounded-xl shadow-sm p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Car className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {[v.year, v.color, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle'}
                      {v.is_primary && <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">Primary</span>}
                    </p>
                    <p className="text-sm text-gray-500">Plate: {v.plate_state ? `${v.plate_state} ` : ''}{v.license_plate}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(v)} className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(v.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h3 className="font-semibold">{editId ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <input type="number" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="2024" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <input type="text" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Black" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                    <input type="text" value={form.make} onChange={(e) => setForm({...form, make: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Toyota" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                    <input type="text" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="Camry" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">License Plate *</label>
                    <input type="text" value={form.license_plate} onChange={(e) => setForm({...form, license_plate: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input type="text" value={form.plate_state} onChange={(e) => setForm({...form, plate_state: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="LA" maxLength={2} />
                  </div>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({...form, is_primary: e.target.checked})} className="rounded" />
                  <span className="text-sm text-gray-700">Primary vehicle</span>
                </label>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                  <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
